import React, { useState, useEffect, useRef } from 'react';
import { 
  Search, 
  Filter, 
  MoreVertical, 
  Edit, 
  Trash2, 
  Eye, 
  UserCheck, 
  UserX,
  Download,
  Upload,
  Users,
  GraduationCap,
  Shield,
  Calendar,
  MapPin,
  Mail,
  Phone,
  User as UserIcon,
  KeyRound
} from 'lucide-react';
import type { User } from '../types';

interface UserListProps {
  users: User[];
  isLoading: boolean;
  filters: any;
  onFilterChange: (key: string, value: string) => void;
  onEditUser: (user: User) => void;
  onDeleteUser: (userId: string, userName: string) => void;
  onToggleStatus: (userId: string, userName: string, currentStatus: string) => void;
  onViewUser: (user: User) => void;
  onResetPassword: (userId: string, userName: string) => void;
  pagination?: {
    current_page: number;
    per_page: number;
    total: number;
    last_page: number;
  };
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void;
}

const USER_ROLES = [
  { value: 'student', label: 'Học sinh', icon: GraduationCap, color: 'bg-blue-100 text-blue-800' },
  { value: 'teacher', label: 'Giáo viên', icon: Users, color: 'bg-green-100 text-green-800' },
  { value: 'publisher', label: 'Nhà xuất bản', icon: Shield, color: 'bg-purple-100 text-purple-800' },
  { value: 'admin', label: 'Quản trị viên', icon: Shield, color: 'bg-red-100 text-red-800' },
  { value: 'super_admin', label: 'Super Admin', icon: Shield, color: 'bg-orange-100 text-orange-800' }
];

const USER_STATUSES = [
  { value: 'active', label: 'Hoạt động', color: 'bg-green-100 text-green-800' },
  { value: 'inactive', label: 'Không hoạt động', color: 'bg-gray-100 text-gray-800' },
  { value: 'pending_verification', label: 'Chờ xác minh', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'suspended', label: 'Tạm khóa', color: 'bg-red-100 text-red-800' }
];

const UserList: React.FC<UserListProps> = ({
  users,
  isLoading,
  filters,
  onFilterChange,
  onEditUser,
  onDeleteUser,
  onToggleStatus,
  onViewUser,
  onResetPassword,
  pagination,
  onPageChange,
  onPerPageChange
}) => {
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

  const getRoleInfo = (user: User) => {
    if (user.roles?.includes('super_admin')) {
      return USER_ROLES.find(r => r.value === 'super_admin') || USER_ROLES[0];
    }
    return USER_ROLES.find(r => r.value === user.role) || USER_ROLES[0];
  };

  const getStatusInfo = (status?: string) => {
    return USER_STATUSES.find(s => s.value === status) || USER_STATUSES[0];
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('vi-VN');
  };

  const handleSelectUser = (userId: string) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (!Array.isArray(users)) return;
    
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(user => user.id || user.user_name || user.code || ''));
    }
  };

  const tableScrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    tableScrollRef.current?.scrollTo({ top: 0 });
  }, [pagination?.current_page, pagination?.per_page, filters]);

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow">
        <div className="p-6">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-16 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Filters */}
      <div className="p-6 border-b">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Tìm kiếm theo tên, tên đăng nhập, email, mã người dùng..."
              value={filters.search || ''}
              onChange={(e) => onFilterChange('search', e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <select
            value={filters.role || ''}
            onChange={(e) => onFilterChange('role', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Tất cả vai trò</option>
            {USER_ROLES.map(role => (
              <option key={role.value} value={role.value}>
                {role.label}
              </option>
            ))}
          </select>

          <select
            value={filters.status || ''}
            onChange={(e) => onFilterChange('status', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Tất cả trạng thái</option>
            {USER_STATUSES.map(status => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>

          <div className="flex space-x-2">
            <button
              onClick={() => onFilterChange('search', '')}
              className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            >
              <Filter className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Table — 530px: tiêu đề, 4 thẻ thống kê, bộ lọc và phân trang của trang Người dùng */}
      <div ref={tableScrollRef} className="overflow-auto max-h-[max(420px,calc(100vh-530px))]">
        <table className="w-full">
          <thead className="bg-gray-50 sticky top-0 z-10 [&_th]:shadow-[0_1px_0_0_#e5e7eb]">
            <tr>
              <th className="px-6 py-3 text-left">
                <input
                  type="checkbox"
                  checked={Array.isArray(users) && selectedUsers.length === users.length && users.length > 0}
                  onChange={handleSelectAll}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Người dùng
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Vai trò
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Trạng thái
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Thông tin liên hệ
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ngày tạo
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Thao tác
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {Array.isArray(users) ? users.map((user) => {
              const roleInfo = getRoleInfo(user);
              const userStatus = user.status || (user.active ? 'active' : 'inactive');
              const statusInfo = getStatusInfo(userStatus);
              
              return (
                <tr key={user.id || user.user_name || user.code || Math.random()} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user.id || user.user_name || user.code || '')}
                      onChange={() => handleSelectUser(user.id || user.user_name || user.code || '')}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </td>
                  
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        {user.avatar ? (
                          <img className="h-10 w-10 rounded-full" src={user.avatar} alt="" />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                            <UserIcon className="h-5 w-5 text-gray-600" />
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {user.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {user.id ? `ID: ${user.id}` : user.user_name ? `User: ${user.user_name}` : user.code ? `Code: ${user.code}` : ''}
                        </div>
                        {/* Show additional info based on role */}
                        {(user.role === 'student' || user.type === 'student') && (
                          <div className="text-xs text-gray-400">
                            {user.grade && `Khối ${user.grade}`}
                            {user.class && ` - Lớp ${user.class}`}
                          </div>
                        )}
                        {(user.role === 'teacher' || user.type === 'teacher') && (
                          <div className="text-xs text-gray-400">
                            {user.unit && user.unit}
                            {user.position && ` - ${user.position}`}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>

                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${roleInfo.color}`}>
                      <roleInfo.icon className="w-3 h-3 mr-1" />
                      {roleInfo.label}
                    </span>
                  </td>

                  <td className="px-6 py-4">
                    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
                      {statusInfo.label}
                    </span>
                  </td>

                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      {user.email && (
                        <div className="flex items-center text-gray-500">
                          <Mail className="w-3 h-3 mr-1" />
                          {user.email}
                        </div>
                      )}
                      {user.phone && (
                        <div className="flex items-center text-gray-500 mt-1">
                          <Phone className="w-3 h-3 mr-1" />
                          {user.phone}
                        </div>
                      )}
                      {user.user_name && !user.email && (
                        <div className="flex items-center text-gray-500">
                          <UserIcon className="w-3 h-3 mr-1" />
                          {user.user_name}
                        </div>
                      )}
                      {user.location && (
                        <div className="flex items-center text-gray-500 mt-1">
                          <MapPin className="w-3 h-3 mr-1" />
                          {user.location}
                        </div>
                      )}
                    </div>
                  </td>

                  <td className="px-6 py-4">
                    <div className="flex items-center text-sm text-gray-500">
                      <Calendar className="w-3 h-3 mr-1" />
                      {formatDate(user.created_at ?? user.createdAt)}
                    </div>
                  </td>

                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => onViewUser(user)}
                        className="text-gray-400 hover:text-blue-600"
                        title="Xem chi tiết"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onEditUser(user)}
                        className="text-gray-400 hover:text-green-600"
                        title="Chỉnh sửa"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onResetPassword(user.id || user.user_name || user.code || '', user.name)}
                        className="text-gray-400 hover:text-orange-600"
                        title="Đặt lại mật khẩu"
                      >
                        <KeyRound className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onToggleStatus(user.id || user.user_name || user.code || '', user.name, userStatus)}
                        className={`${
                          userStatus === 'active' 
                            ? 'text-gray-400 hover:text-yellow-600' 
                            : 'text-gray-400 hover:text-green-600'
                        }`}
                        title={userStatus === 'active' ? 'Tạm dừng' : 'Kích hoạt'}
                      >
                        {userStatus === 'active' ? (
                          <UserX className="w-4 h-4" />
                        ) : (
                          <UserCheck className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={() => onDeleteUser(user.id || user.user_name || user.code || '', user.name)}
                        className="text-gray-400 hover:text-red-600"
                        title="Xóa"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            }) : (
              <tr>
                <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                  Không có dữ liệu người dùng
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && (
        <div className="px-6 py-4 border-t bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center text-sm text-gray-700">
              <span>
                Hiển thị {((pagination.current_page - 1) * pagination.per_page) + 1} đến{' '}
                {Math.min(pagination.current_page * pagination.per_page, pagination.total)} trong tổng số{' '}
                {pagination.total} kết quả
              </span>
            </div>
            
            <div className="flex items-center space-x-2">
              <select
                value={pagination.per_page}
                onChange={(e) => onPerPageChange(Number(e.target.value))}
                className="px-2 py-1 border border-gray-300 rounded text-sm"
              >
                <option value={10}>10/trang</option>
                <option value={15}>15/trang</option>
                <option value={25}>25/trang</option>
                <option value={50}>50/trang</option>
              </select>
              
              <div className="flex space-x-1">
                <button
                  onClick={() => onPageChange(pagination.current_page - 1)}
                  disabled={pagination.current_page <= 1}
                  className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Trước
                </button>
                
                {(() => {
                  const currentPage = pagination.current_page;
                  const totalPages = pagination.last_page;
                  const pages = [];
                  
                  // Calculate which pages to show
                  let startPage = Math.max(1, currentPage - 2);
                  let endPage = Math.min(totalPages, currentPage + 2);
                  
                  // Adjust if we're near the beginning or end
                  if (endPage - startPage < 4) {
                    if (startPage === 1) {
                      endPage = Math.min(totalPages, startPage + 4);
                    } else {
                      startPage = Math.max(1, endPage - 4);
                    }
                  }
                  
                  // Add first page and ellipsis if needed
                  if (startPage > 1) {
                    pages.push(
                      <button
                        key={1}
                        onClick={() => onPageChange(1)}
                        className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50"
                      >
                        1
                      </button>
                    );
                    if (startPage > 2) {
                      pages.push(
                        <span key="ellipsis1" className="px-2 text-gray-500">
                          ...
                        </span>
                      );
                    }
                  }
                  
                  // Add page numbers
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <button
                        key={i}
                        onClick={() => onPageChange(i)}
                        className={`px-3 py-1 border rounded text-sm ${
                          i === currentPage
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {i}
                      </button>
                    );
                  }
                  
                  // Add ellipsis and last page if needed
                  if (endPage < totalPages) {
                    if (endPage < totalPages - 1) {
                      pages.push(
                        <span key="ellipsis2" className="px-2 text-gray-500">
                          ...
                        </span>
                      );
                    }
                    pages.push(
                      <button
                        key={totalPages}
                        onClick={() => onPageChange(totalPages)}
                        className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50"
                      >
                        {totalPages}
                      </button>
                    );
                  }
                  
                  return pages;
                })()}
                
                <button
                  onClick={() => onPageChange(pagination.current_page + 1)}
                  disabled={pagination.current_page >= pagination.last_page}
                  className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Sau
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserList;
